import React, { useEffect, useState } from 'react';
import { Rnd } from 'react-rnd';
import { Trash2, Save, ArrowLeft, Type, Image as ImageIcon, Clock, List as ListIcon, Square, Code, Wand2, Database } from 'lucide-react';
import { CANVAS, WIDGET_TYPES, newWidget, emptyTemplate, WidgetContent, useElementWidth } from './TemplateRenderer';
import { extractVariables, widgetVariables, countConvertibleStrings, convertWidgetStringsToVars } from './templateVariables';
import TemplateApiModal from './TemplateApiModal';
import ConfirmDialog from './ConfirmDialog';
import { rowKeys, columnsToText, textToColumns, columnsFromKeys } from './datafeedColumns';
import { api } from '../api';

const TYPE_ICONS = { text: Type, image: ImageIcon, clock: Clock, list: ListIcon, shape: Square, datafeed: Database, html: Code };

export default function TemplateEditor({ template, userId, onSaved, onCancel }) {
  const [name, setName] = useState(template?.name || 'Untitled template');
  const initial = template?.json || emptyTemplate();
  const [background, setBackground] = useState(initial.background || '#0b1020');
  const [widgets, setWidgets] = useState(initial.widgets || []);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showApi, setShowApi] = useState(false);
  // Set right after creating a new template: shows the API modal with the
  // real template id before handing control back to the list.
  const [justCreated, setJustCreated] = useState(null);
  const [confirmConvert, setConfirmConvert] = useState(false);
  // Datafeed widgets bind to a data source by id; the picker needs the names.
  // A failure here is not fatal — the inspector falls back to a plain id field.
  const [sources, setSources] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.listDataSources(userId)
      .then((res) => { if (!cancelled) setSources(res.data_sources || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userId]);

  // The canvas scales to whatever width the layout gives its column.
  const [canvasRef, canvasWidth] = useElementWidth();
  const scale = (canvasWidth || CANVAS.w) / CANVAS.w;

  const selected = widgets.find((w) => w.id === selectedId) || null;
  const currentJson = { version: 1, background, widgets };
  const templateVars = extractVariables(currentJson);
  const convertibleCount = countConvertibleStrings(widgets);

  // Turn fixed text into {{variables}} named after the text it replaces —
  // either across the whole template or for a single widget.
  const makeAllVariables = () => {
    const used = new Set(templateVars);
    setWidgets((prev) => prev.map((w) => convertWidgetStringsToVars(w, used)));
    setConfirmConvert(false);
  };

  const makeWidgetVariables = (id) => {
    const used = new Set(templateVars);
    setWidgets((prev) => prev.map((w) => (w.id === id ? convertWidgetStringsToVars(w, used) : w)));
  };

  const addWidget = (type) => {
    const w = newWidget(type);
    setWidgets((prev) => [...prev, w]);
    setSelectedId(w.id);
  };

  const updateWidget = (id, patch) =>
    setWidgets((prev) => prev.map((w) => (w.id === id ? { ...w, ...patch } : w)));

  const updateProps = (id, propPatch) =>
    setWidgets((prev) => prev.map((w) => (w.id === id ? { ...w, props: { ...w.props, ...propPatch } } : w)));

  const removeWidget = (id) => {
    setWidgets((prev) => prev.filter((w) => w.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const json = { version: 1, background, widgets };
      const result = template?.id
        ? await api.updateTemplate(template.id, { name, json })
        : await api.createTemplate({ name, json, userId });
      if (template?.id) {
        onSaved && onSaved(result.template);
      } else {
        // New template: show its API calls (with the real id) before leaving.
        setJustCreated(result.template);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="editor-bar">
        <button className="btn btn-ghost btn-icon" onClick={onCancel} title="Back to templates">
          <ArrowLeft size={16} />
        </button>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        {error && <span className="error-text">{error}</span>}
        <button className="btn btn-ghost" style={{ marginLeft: 'auto' }} onClick={() => setShowApi(true)}
          title="Show the API calls that drive this template">
          <Code size={16} /> API
        </button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          <Save size={16} /> {saving ? 'Saving…' : 'Save template'}
        </button>
      </div>

      <div className="editor-grid">
        {/* Palette */}
        <div className="palette">
          <div className="palette-title">Add widget</div>
          {WIDGET_TYPES.map((t) => {
            const Icon = TYPE_ICONS[t.type] || Square;
            return (
              <button key={t.type} className="palette-btn" onClick={() => addWidget(t.type)}>
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
          <div style={{ marginTop: 14 }}>
            <label className="label">Background</label>
            <input type="color" className="input-color" value={background}
              onChange={(e) => setBackground(e.target.value)} />
          </div>
          <div style={{ marginTop: 14 }}>
            <label className="label">API variables</label>
            {templateVars.length === 0 ? (
              <div className="props-hint">
                None yet — wrap any text in {'{{name}}'} to make it fillable via the API.
              </div>
            ) : (
              <div className="chip-row">
                {templateVars.map((v) => <span key={v} className="chip chip-var">{`{{${v}}}`}</span>)}
              </div>
            )}
            {convertibleCount > 0 && (
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 8, width: '100%' }}
                onClick={() => setConfirmConvert(true)}
                title="Convert every fixed text field into an API variable named after its text">
                <Wand2 size={16} /> Make all text variable
              </button>
            )}
          </div>
        </div>

        {/* Canvas */}
        <div className="canvas-col" ref={canvasRef}>
          {canvasWidth > 0 && (
            <div className="canvas-frame" style={{ width: canvasWidth, height: CANVAS.h * scale }}
              onMouseDown={(e) => { if (e.target === e.currentTarget) setSelectedId(null); }}>
              <div style={{ width: CANVAS.w, height: CANVAS.h, transform: `scale(${scale})`,
                transformOrigin: 'top left', background, position: 'absolute' }}>
                {widgets.map((w) => (
                  <Rnd key={w.id} scale={scale} bounds="parent"
                    size={{ width: w.w, height: w.h }} position={{ x: w.x, y: w.y }}
                    onDragStart={() => setSelectedId(w.id)}
                    onDragStop={(e, d) => updateWidget(w.id, { x: Math.round(d.x), y: Math.round(d.y) })}
                    onResizeStop={(e, dir, ref, delta, pos) => updateWidget(w.id, {
                      w: Math.round(parseFloat(ref.style.width)),
                      h: Math.round(parseFloat(ref.style.height)),
                      x: Math.round(pos.x), y: Math.round(pos.y),
                    })}
                    style={{ outline: selectedId === w.id ? '3px solid var(--brand)' : '1px dashed rgba(255,255,255,0.25)' }}
                    onMouseDown={() => setSelectedId(w.id)}>
                    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                      <WidgetContent widget={w} />
                      {widgetVariables(w).length > 0 && <div className="var-badge">{'{{…}}'}</div>}
                    </div>
                  </Rnd>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Properties */}
        <div className="props-panel">
          <div className="palette-title">Properties</div>
          {!selected && <div className="props-hint">Select a widget on the canvas to edit its properties.</div>}
          {selected && (
            <div>
              <div className="props-head">
                <span className="type">{selected.type}</span>
                <button className="btn btn-danger btn-sm btn-icon" onClick={() => removeWidget(selected.id)} title="Delete widget">
                  <Trash2 size={16} />
                </button>
              </div>
              <WidgetVarHint widget={selected} onConvert={() => makeWidgetVariables(selected.id)} />
              <WidgetProps widget={selected} updateProps={updateProps} sources={sources} />
              <PositionFields widget={selected} updateWidget={updateWidget} />
            </div>
          )}
        </div>
      </div>

      {showApi && (
        <TemplateApiModal
          template={{ id: template?.id, name, json: currentJson }}
          onClose={() => setShowApi(false)}
        />
      )}
      {justCreated && (
        <TemplateApiModal
          template={justCreated}
          heading="Template saved — here’s how to drive it"
          onClose={() => { const t = justCreated; setJustCreated(null); onSaved && onSaved(t); }}
        />
      )}
      <ConfirmDialog
        open={confirmConvert}
        title={`Make ${convertibleCount} text field${convertibleCount === 1 ? '' : 's'} variable?`}
        body="Each fixed text becomes an API variable named after it (lists are named by their title). Close without saving to undo."
        confirmLabel="Convert"
        onConfirm={makeAllVariables}
        onCancel={() => setConfirmConvert(false)}
      />
    </div>
  );
}

// Answers "is this widget fixed or API-driven?" at a glance, with a shortcut
// to convert its fixed text into variables.
function WidgetVarHint({ widget, onConvert }) {
  // A datafeed is neither fixed nor variable-driven — its content comes from a
  // data source, which its own inspector explains.
  if (widget.type === 'datafeed') return null;

  const vars = widgetVariables(widget);
  const convertible = countConvertibleStrings([widget]);
  if (vars.length === 0) {
    return (
      <div style={{ marginBottom: 10 }}>
        <div className="props-hint">
          Fixed content — wrap text in {'{{name}}'} to make it an API variable.
        </div>
        {convertible > 0 && (
          <button className="btn btn-ghost btn-sm" style={{ marginTop: 6 }} onClick={onConvert}
            title="Convert this widget's text into API variables named after the text">
            <Wand2 size={16} /> Make variable
          </button>
        )}
      </div>
    );
  }
  return (
    <div style={{ marginBottom: 10 }}>
      <div className="chip-row">
        {vars.map((v) => <span key={v} className="chip chip-var">{`{{${v}}}`}</span>)}
      </div>
      <div className="props-hint" style={{ marginTop: 6 }}>
        Variable content — the API fills these at push time.
      </div>
      {convertible > 0 && (
        <button className="btn btn-ghost btn-sm" style={{ marginTop: 6 }} onClick={onConvert}
          title="Convert this widget's remaining fixed text into API variables">
          <Wand2 size={16} /> Make remaining text variable
        </button>
      )}
    </div>
  );
}

function WidgetProps({ widget, updateProps, sources = [] }) {
  const p = widget.props || {};
  const set = (patch) => updateProps(widget.id, patch);
  switch (widget.type) {
    case 'text':
      return (
        <>
          <div className="field">
            <label className="label">Text</label>
            <textarea className="textarea" style={{ height: 80 }} value={p.text || ''}
              onChange={(e) => set({ text: e.target.value })} />
          </div>
          <ColorRow label="Color" value={p.color} onChange={(v) => set({ color: v })} />
          <NumRow label="Font size" value={p.fontSize} onChange={(v) => set({ fontSize: v })} />
          <SelectRow label="Align" value={p.textAlign} options={['left', 'center', 'right']} onChange={(v) => set({ textAlign: v })} />
          <SelectRow label="Weight" value={p.fontWeight} options={['400', '600', '700']} onChange={(v) => set({ fontWeight: v })} />
        </>
      );
    case 'image':
      return (
        <>
          <div className="field">
            <label className="label">Image URL</label>
            <input className="input" value={p.src || ''} onChange={(e) => set({ src: e.target.value })} placeholder="https://…" />
          </div>
          <SelectRow label="Fit" value={p.fit} options={['cover', 'contain', 'fill']} onChange={(v) => set({ fit: v })} />
        </>
      );
    case 'clock':
      return (
        <>
          <SelectRow label="Format" value={p.format} options={['24h', '12h']} onChange={(v) => set({ format: v })} />
          <SelectRow label="Show date" value={p.showDate === false ? 'no' : 'yes'} options={['yes', 'no']} onChange={(v) => set({ showDate: v === 'yes' })} />
          <ColorRow label="Color" value={p.color} onChange={(v) => set({ color: v })} />
          <NumRow label="Font size" value={p.fontSize} onChange={(v) => set({ fontSize: v })} />
        </>
      );
    case 'list':
      return (
        <>
          <div className="field">
            <label className="label">Title</label>
            <input className="input" value={p.title || ''} onChange={(e) => set({ title: e.target.value })} />
          </div>
          <div className="field">
            <label className="label">Items (one per line)</label>
            <textarea className="textarea" style={{ height: 120 }}
              value={(p.items || []).map((i) => (typeof i === 'object' ? `${i.label} | ${i.value}` : i)).join('\n')}
              onChange={(e) => set({ items: e.target.value.split('\n').filter(Boolean).map((line) => {
                const parts = line.split('|');
                return parts.length > 1 ? { label: parts[0].trim(), value: parts.slice(1).join('|').trim() } : line;
              }) })}
              placeholder={'Plain line\nLabel | Value'} />
          </div>
          <ColorRow label="Color" value={p.color} onChange={(v) => set({ color: v })} />
          <NumRow label="Font size" value={p.fontSize} onChange={(v) => set({ fontSize: v })} />
        </>
      );
    case 'shape':
      return (
        <>
          <ColorRow label="Fill" value={p.background} onChange={(v) => set({ background: v })} />
          <NumRow label="Corner radius" value={p.borderRadius} onChange={(v) => set({ borderRadius: v })} />
        </>
      );
    case 'datafeed':
      return <DatafeedProps p={p} set={set} sources={sources} />;
    case 'html':
      return (
        <div className="field">
          <label className="label">HTML</label>
          <textarea className="textarea" style={{ height: 160, fontFamily: 'ui-monospace, monospace' }}
            value={p.html || ''} onChange={(e) => set({ html: e.target.value })} />
        </div>
      );
    default:
      return null;
  }
}

function DatafeedProps({ p, set, sources }) {
  const source = sources.find((s) => s.id === p.sourceId) || null;
  const liveRows = Array.isArray(source?.last_data) ? source.last_data : [];
  const keys = rowKeys(source?.last_data);

  // Adopt the source's real shape: columns named after its fields, and a few
  // real rows as sample data so the canvas shows what will actually appear.
  const useLiveShape = () => set({
    columns: columnsFromKeys(keys),
    sampleData: liveRows.slice(0, 6),
  });

  return (
    <>
      <div className="field">
        <label className="label">Data source</label>
        <select className="select" value={p.sourceId || ''} onChange={(e) => set({ sourceId: e.target.value })}>
          <option value="">— none (sample data only) —</option>
          {sources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {sources.length === 0 && (
          <div className="props-hint" style={{ marginTop: 6 }}>
            No data sources yet — create one on the Data sources page, then pick it here.
          </div>
        )}
        {source && (
          <div className="props-hint" style={{ marginTop: 6 }}>
            {source.last_error
              ? <span className="error-text">Last fetch failed: {source.last_error}</span>
              : `Live: ${liveRows.length} row${liveRows.length === 1 ? '' : 's'}, updates every ${source.refresh_seconds}s.`}
          </div>
        )}
        {keys.length > 0 && (
          <>
            <div className="chip-row" style={{ marginTop: 8 }}>
              {keys.map((k) => <span key={k} className="chip chip-var">{k}</span>)}
            </div>
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 8, width: '100%' }}
              onClick={useLiveShape}
              title="Replace the columns below with this source's fields, and preview its real rows">
              <Wand2 size={16} /> Use these columns
            </button>
          </>
        )}
      </div>
      <div className="field">
        <label className="label">Title</label>
        <input className="input" value={p.title || ''} onChange={(e) => set({ title: e.target.value })} />
      </div>
      <div className="field">
        <label className="label">Columns (one per line)</label>
        <textarea className="textarea" style={{ height: 110, fontFamily: 'ui-monospace, monospace' }}
          value={columnsToText(p.columns)}
          onChange={(e) => set({ columns: textToColumns(e.target.value, p.columns) })}
          placeholder={'key\nkey | HEADER'} />
        <div className="props-hint" style={{ marginTop: 4 }}>
          Each key must match a field on the source's rows.
        </div>
      </div>
      <NumRow label="Max rows" value={p.maxRows} onChange={(v) => set({ maxRows: v })} />
      <ColorRow label="Color" value={p.color} onChange={(v) => set({ color: v })} />
      <NumRow label="Font size" value={p.fontSize} onChange={(v) => set({ fontSize: v })} />
    </>
  );
}

function PositionFields({ widget, updateWidget }) {
  const num = (k) => (e) => updateWidget(widget.id, { [k]: parseInt(e.target.value || '0', 10) });
  return (
    <div className="field-row" style={{ marginTop: 10 }}>
      {['x', 'y', 'w', 'h'].map((k) => (
        <div key={k}>
          <label className="label">{k.toUpperCase()}</label>
          <input type="number" className="input" value={widget[k]} onChange={num(k)} />
        </div>
      ))}
    </div>
  );
}

const ColorRow = ({ label, value, onChange }) => (
  <div className="field">
    <label className="label">{label}</label>
    <input type="color" className="input-color" value={value || '#ffffff'} onChange={(e) => onChange(e.target.value)} />
  </div>
);
const NumRow = ({ label, value, onChange }) => (
  <div className="field">
    <label className="label">{label}</label>
    <input type="number" className="input" value={value ?? ''} onChange={(e) => onChange(parseInt(e.target.value || '0', 10))} />
  </div>
);
const SelectRow = ({ label, value, options, onChange }) => (
  <div className="field">
    <label className="label">{label}</label>
    <select className="select" value={value || options[0]} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
